import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ShoppingBag,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Plus,
} from "lucide-react";
import PitBoardLogo from "@/components/PitBoardLogo";
import PackRequestModal from "@/components/PackRequestModal";

/**
 * Page publique de vitrine des Packs Métiers.
 * Accessible sans authentification — argument de conversion SEO.
 * Pas de bouton "Acheter" : redirige vers /login.
 */
const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export default function PacksVitrine() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestOpen, setRequestOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/packs");
        if (!cancel) setCatalog(data.packs || []);
      } catch {
        // public endpoint, on ignore — la page restera vide
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--carbon)" }} data-testid="packs-vitrine-page">
      {/* Topbar simple */}
      <header
        className="border-b sticky top-0 z-10"
        style={{
          background: "rgba(20,20,20,0.92)",
          backdropFilter: "blur(12px)",
          borderColor: "var(--steel)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="vitrine-logo-link">
            <PitBoardLogo size={36} />
          </Link>
          <nav className="flex items-center gap-2">
            <Link
              to="/"
              className="text-[13px] inline-flex items-center gap-1.5 hover:underline"
              style={{ color: "var(--ink-soft)" }}
              data-testid="vitrine-back-home"
            >
              <ArrowLeft size={14} /> Accueil
            </Link>
            <Link
              to="/login"
              className="btn btn-primary"
              style={{ minHeight: 40, fontSize: "0.85rem", textTransform: "none", letterSpacing: 0 }}
              data-testid="vitrine-cta-login"
            >
              Se connecter
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
        <div className="inline-flex items-center gap-2 mb-4 text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--pit-yellow)" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--pit-yellow)" }} />
          Bibliothèques métiers prêtes à l&apos;emploi
        </div>
        <h1
          className="font-display tracking-tighter leading-[0.95] mb-6"
          style={{ color: "var(--chalk)", fontSize: "clamp(2.4rem, 6vw, 4.5rem)" }}
        >
          PACKS MÉTIERS.<br />
          <span style={{ color: "var(--pit-yellow)" }}>700+ OUVRAGES.</span>{" "}
          <span style={{ color: "var(--board-red)" }}>PRÊTS À CHIFFRER.</span>
        </h1>
        <p className="font-body max-w-2xl text-base leading-relaxed mb-3" style={{ color: "var(--ink-soft)" }}>
          Une seule fois <b>49 € HT</b> (58,80 € TTC). Importez en un clic la base de prix complète de votre métier —
          prix indicatifs, temps main d&apos;œuvre, matériaux pré-renseignés. Vous adaptez à vos tarifs et vous chiffrez 10× plus vite.
        </p>
        <p className="text-[12px]" style={{ color: "var(--grey)" }}>
          Conforme DTU · prix marché 2025-2026 · catalogue mis à jour gratuitement (refresh illimité)
        </p>
      </section>

      {/* Avertissement légal */}
      <section className="max-w-6xl mx-auto px-6 mb-10">
        <div
          className="rounded-lg border p-4 flex items-start gap-3"
          style={{ background: "rgba(255,196,0,0.05)", borderColor: "rgba(255,196,0,0.30)" }}
        >
          <AlertCircle size={16} style={{ color: "var(--pit-yellow)" }} className="shrink-0 mt-0.5" />
          <div className="text-[12px] leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            <b>Prix, temps, matériaux et consommations fournis à titre purement indicatif</b> (CGV Art. 11.3) — non
            contractuels. Adaptez systématiquement les tarifs, durées et quantités à votre zone géographique,
            votre clientèle et vos coûts réels avant émission de devis.
          </div>
        </div>
      </section>

      {/* Catalogue */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="text-center py-16" style={{ color: "var(--grey)" }}>
            <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Chargement du catalogue…
          </div>
        ) : catalog.length === 0 ? (
          <div className="text-center py-16" style={{ color: "var(--grey)" }}>
            Catalogue temporairement indisponible.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="vitrine-catalog-grid">
            {catalog.map((p) => (
              <article
                key={p.id}
                className="card p-5 flex flex-col gap-4 transition-transform hover:-translate-y-1 hover:shadow-lg"
                style={{ borderColor: "var(--steel)" }}
                data-testid={`vitrine-pack-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--grey)" }}>
                      {p.trade}
                    </div>
                    <h3 className="font-display text-xl tracking-tight leading-tight" style={{ color: "var(--chalk)" }}>
                      {p.name}
                    </h3>
                    <p className="text-[13px] mt-1" style={{ color: "var(--ink-soft)" }}>{p.tagline}</p>
                  </div>
                  <ShoppingBag size={22} style={{ color: "var(--pit-yellow)" }} />
                </div>

                <p className="text-[12px] leading-relaxed" style={{ color: "var(--grey)" }}>{p.description}</p>

                <ul className="space-y-1.5">
                  {(p.bullets || []).map((b, i) => (
                    <li key={i} className="text-[13px] flex items-start gap-2" style={{ color: "var(--ink-soft)" }}>
                      <CheckCircle2 size={13} className="mt-0.5 shrink-0" style={{ color: "var(--pit-yellow)" }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div
                  className="mt-1 p-3 rounded-lg border flex items-baseline justify-between"
                  style={{ background: "var(--carbon)", borderColor: "var(--steel)" }}
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Prix</div>
                    <div className="font-display text-2xl" style={{ color: "var(--chalk)" }}>
                      {fmtEUR(p.price_eur_ht ?? 49)} <span className="text-sm font-normal" style={{ color: "var(--ink-soft)" }}>HT</span>
                    </div>
                    <div className="text-[10px]" style={{ color: "var(--grey)" }}>Soit {fmtEUR(p.price_eur_ttc)} TTC · paiement unique</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Contenu</div>
                    <div className="font-display text-2xl" style={{ color: "var(--pit-yellow)" }}>{p.items_count}</div>
                    <div className="text-[10px]" style={{ color: "var(--grey)" }}>ouvrages</div>
                  </div>
                </div>

                <Link
                  to="/login"
                  className="btn btn-outline w-full"
                  data-testid={`vitrine-cta-${p.id}`}
                >
                  Se connecter pour acheter <ArrowRight size={14} />
                </Link>
              </article>
            ))}
          </div>
        )}

        {/* Bloc "Demander un pack métier" — placé sous le catalogue */}
        {!loading && (
          <div
            className="mt-8 rounded-xl border-2 border-dashed p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
            style={{ borderColor: "rgba(255,196,0,0.4)", background: "rgba(255,196,0,0.04)" }}
            data-testid="vitrine-pack-request-block"
          >
            <div className="text-center sm:text-left">
              <h3 className="font-display text-xl sm:text-2xl mb-1.5" style={{ color: "var(--chalk)", fontWeight: 700 }}>
                Vous ne trouvez pas votre pack ?
              </h3>
              <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
                Dites-le nous — on peut l&apos;ajouter à la roadmap.
              </p>
            </div>
            <button
              onClick={() => setRequestOpen(true)}
              className="btn btn-primary shrink-0 inline-flex"
              style={{ minHeight: 48, padding: "0 22px", fontSize: "0.9rem", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}
              data-testid="vitrine-request-pack-btn"
            >
              <Plus size={15} /> Demander un pack
            </button>
          </div>
        )}
      </section>

      {/* Bandeau CTA bas */}
      <section
        className="border-t"
        style={{ background: "var(--carbon-2)", borderColor: "var(--steel)" }}
      >
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-4" style={{ color: "var(--chalk)" }}>
            Prêt à chiffrer comme un pro ?
          </h2>
          <p className="text-[15px] leading-relaxed mb-7" style={{ color: "var(--ink-soft)" }}>
            Créez votre compte gratuit PitBoard en 30 secondes. Pas de CB demandée à l&apos;inscription —
            les Packs Métiers sont optionnels et payés à la pièce uniquement si vous le souhaitez.
          </p>
          <Link
            to="/login"
            className="btn btn-primary inline-flex"
            style={{ minHeight: 54, padding: "0 28px", textTransform: "none", letterSpacing: 0, fontSize: "0.95rem", fontWeight: 600 }}
            data-testid="vitrine-cta-footer"
          >
            Démarrer maintenant <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="border-t py-6"
        style={{ borderColor: "var(--steel)", background: "var(--carbon)" }}
      >
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-3 text-[11px] uppercase tracking-wider" style={{ color: "var(--grey)" }}>
          <span>© 2026 PITBOARD · ENTREPRENEURS FRANÇAIS</span>
          <div className="flex gap-4">
            <Link to="/cgv" className="hover:underline">CGV</Link>
            <Link to="/mentions-legales" className="hover:underline">Mentions légales</Link>
            <Link to="/confidentialite" className="hover:underline">Confidentialité</Link>
          </div>
        </div>
      </footer>

      <PackRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </div>
  );
}
